import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Users, Stethoscope, TrendingUp, Award, ArrowRight, Shield, Wallet } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_therapist-hub-36/artifacts/w11vyo5q_Physiomobile%20Logo.jpeg";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function LoginPage() {
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already authenticated
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(response => {
        if (response.data.role === "admin") {
          navigate("/admin");
        } else {
          navigate("/therapist");
        }
      })
      .catch(() => {
        // Not authenticated, stay on login page
      });
  }, [navigate]);

  const handleLogin = (role) => {
    // Store intended role in sessionStorage for after auth
    sessionStorage.setItem('intended_role', role);
    const redirectUrl = window.location.origin + '/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50" data-testid="login-page">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="Physiomobile" 
              className="h-12 w-auto"
            />
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600 hidden md:block">Malaysia's #1 Healthcare Platform</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 rounded-full mb-6 animate-fadeIn">
            <span className="w-2 h-2 bg-primary rounded-full animate-pulse-soft"></span>
            <span className="text-sm font-medium text-primary">Therapist Lifecycle Management System</span>
          </div>
          
          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-tight mb-6 animate-fadeIn">
            Empower Healthcare<br />
            <span className="text-primary">Professionals</span>
          </h1>
          
          <p className="text-lg text-slate-600 mb-12 max-w-2xl mx-auto animate-fadeIn">
            The complete platform to attract, onboard, train, deploy, and retain top healthcare professionals across Malaysia.
          </p>
        </div>
      </section>

      {/* Login Options */}
      <section className="px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-center font-heading font-semibold text-2xl text-slate-800 mb-8">
            Choose how you want to sign in
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Admin Login Card */}
            <Card className="relative overflow-hidden border-2 border-slate-200 hover:border-primary hover:shadow-xl transition-all duration-300 cursor-pointer group" data-testid="admin-login-card">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary to-red-400"></div>
              <CardContent className="p-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 bg-primary-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Shield className="w-10 h-10 text-primary" />
                  </div>
                  
                  <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">
                    Admin / Operations
                  </h3>
                  
                  <p className="text-slate-600 mb-6">
                    Manage therapists, track KPIs, handle applications, and oversee platform operations.
                  </p>
                  
                  <div className="space-y-2 text-left w-full mb-6">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                      Dashboard & Analytics
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                      Application Management
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                      Performance & Compliance
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                      Earnings & Reports
                    </div>
                  </div>
                  
                  <Button 
                    onClick={() => handleLogin('admin')}
                    className="w-full bg-primary hover:bg-primary-600 text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
                    data-testid="admin-login-btn"
                  >
                    Sign in as Admin <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Therapist Login Card */}
            <Card className="relative overflow-hidden border-2 border-slate-200 hover:border-emerald-500 hover:shadow-xl transition-all duration-300 cursor-pointer group" data-testid="therapist-login-card">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-emerald-500 to-teal-400"></div>
              <CardContent className="p-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 bg-emerald-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Stethoscope className="w-10 h-10 text-emerald-600" />
                  </div>
                  
                  <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">
                    Therapist / Professional
                  </h3>
                  
                  <p className="text-slate-600 mb-6">
                    Track your performance, manage earnings, grow your career, and connect with patients.
                  </p>
                  
                  <div className="space-y-2 text-left w-full mb-6">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                      Personal Dashboard
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                      Performance Tracking
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                      Earnings & Wallet
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                      Career Growth Path
                    </div>
                  </div>
                  
                  <Button 
                    onClick={() => handleLogin('therapist')}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
                    data-testid="therapist-login-btn"
                  >
                    Sign in as Therapist <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-white" />
              </div>
              <p className="font-heading font-bold text-3xl text-white">2,500+</p>
              <p className="text-slate-400">Active Therapists</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-7 h-7 text-emerald-400" />
              </div>
              <p className="font-heading font-bold text-3xl text-white">94%</p>
              <p className="text-slate-400">Retention Rate</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Award className="w-7 h-7 text-amber-400" />
              </div>
              <p className="font-heading font-bold text-3xl text-white">4.8</p>
              <p className="text-slate-400">Avg. Rating</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Wallet className="w-7 h-7 text-primary" />
              </div>
              <p className="font-heading font-bold text-3xl text-white">RM 5M+</p>
              <p className="text-slate-400">Monthly Payouts</p>
            </div>
          </div>
        </div>
      </section>

      {/* Therapist Types */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-center font-heading font-bold text-2xl text-slate-900 mb-8">
            We Support All Healthcare Professionals
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {['Physiotherapists', 'Occupational Therapists', 'Speech Therapists', 'Dietitians', 'Caregivers'].map((type) => (
              <div key={type} className="px-6 py-3 bg-white border border-slate-200 rounded-full text-slate-700 font-medium shadow-sm">
                {type}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="Physiomobile" 
              className="h-10 w-auto"
            />
          </div>
          <p className="text-sm text-slate-500">
            © 2025 Physiomobile. All rights reserved. Malaysia's Leading Healthcare Platform.
          </p>
        </div>
      </footer>
    </div>
  );
}
